import '../sidebar/newSidebar.scss'
import { Sidebar, Menu, MenuItem, SubMenu, menuClasses } from 'react-pro-sidebar'
import { Link, useLocation } from 'react-router-dom'
import { Box, CssBaseline, Fab, Typography, Link as MuiLink, Divider } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { selectUserMaster } from '../../../features/kyb/kybCountrySlice'
import { useTranslation } from 'react-i18next'
import { IOSSwitch } from '../../utilities/Switches'
import { updateSidebarCollapsed, updateTheme } from '../../../features/theme/themeSlice'
import { menuTheme } from './utils/menuTheme'
import useFilteredSidebarConfig, { sidebarConfig } from './utils/mappedServices'
import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import LaunchIcon from '@mui/icons-material/Launch'

const AppSidebar = ({ activeServices }) => {
  //const [collapsed, setCollapsed] = useState(false)
  const { emailClient } = useSelector((state) => state.login)
  const userMaster = useSelector((state) => selectUserMaster(state, emailClient))
  const { theme: themeGueno, sidebarCollapsed: collapsed } = useSelector((state) => state.theme)
  const dispatch = useDispatch()
  const { t } = useTranslation(['common', 'messages'])
  const filteredSidebarConfig = useFilteredSidebarConfig(sidebarConfig, activeServices)
  const location = useLocation()

  const toggleTheme = () => {
    const newTheme = themeGueno === 'light' ? 'dark' : 'light'
    return dispatch(updateTheme(newTheme))
  }

  const handleDrawer = () => {
    return dispatch(updateSidebarCollapsed(!collapsed))
  }

  const menuItemStyles = {
    subMenuContent: () => ({
      [`&.${menuClasses.open}`]: {
        backgroundColor: menuTheme[themeGueno].menu.menuContent,
        borderRadius: '0.75rem',
        margin: '0 .5rem',
      },
      [`&.${menuClasses.subMenuContent}`]: {
        backgroundColor: menuTheme[themeGueno].menu.menuContent,
      },
    }),
    button: ({ active }) => ({
      backgroundColor: active ? menuTheme[themeGueno].menu.active.backgroundColor : undefined,
      '&:hover': {
        backgroundColor: menuTheme[themeGueno].menu.hover.backgroundColor,
      },
    }),
    icon: () => ({
      marginRight: !collapsed ? '2px' : '',
    }),
  }

  const isActive = (url) => {
    return url !== '/' && location?.pathname?.startsWith(url)
  }

  return (
    <Box className="custom-sidebar-wrapper" sx={{ backgroundColor: menuTheme[themeGueno].sidebar.backgroundColor }}>
      {!collapsed ? (
        <Box className="custom-sidebar-top">
          <Box className="gueno-logo">
            <img
              alt={userMaster?.isNuclea ? 'nuclea-logo' : 'gueno-logo'}
              src={
                userMaster?.isNuclea
                  ? '/logo-nuclea.png'
                  : themeGueno === 'light'
                  ? '/logo-gueno-light2.png'
                  : '/logo-gueno2.png'
              }
            />
          </Box>
          <Fab size="small" sx={{ minWidth: '2.75rem' }} onClick={handleDrawer}>
            {<KeyboardDoubleArrowRightIcon />}
          </Fab>
        </Box>
      ) : (
        <Box className="custom-sidebar-top-collapsed" onClick={handleDrawer}>
          <Box className="gueno-logo-small">
            <img
              alt={userMaster?.isNuclea ? 'nuclea-logo' : 'gueno-logo'}
              src={userMaster?.isNuclea ? '/logo-nuclea-mini.png' : '/logo-gueno-light2-sm.png'}
            />
          </Box>
          <Fab size="small" sx={{ minWidth: '2.75rem' }}>
            {<KeyboardDoubleArrowLeftIcon />}
          </Fab>
        </Box>
      )}
      <Sidebar
        collapsed={collapsed}
        style={{
          width: collapsed ? '80px' : 'auto',
        }}
        className={`pro-sidebar ${
          themeGueno === 'light' ? 'pro-sidebar-scrollbar-light' : 'pro-sidebar-scrollbar-dark'
        }`}
        backgroundColor={menuTheme[themeGueno].sidebar.backgroundColor}
        rootStyles={{
          color: menuTheme[themeGueno].sidebar.color,
        }}
      >
        <CssBaseline />
        <Menu iconShape="circle" menuItemStyles={menuItemStyles}>
          {filteredSidebarConfig.map((service, index) => {
            if (service.routes) {
              return (
                <SubMenu
                  key={index}
                  label={t(service.title)}
                  icon={
                    <Box className="item-icon" style={{ color: themeGueno === 'light' ? '#66766B' : '#919D95' }}>
                      {service.icon}
                    </Box>
                  }
                  defaultOpen={service.routes.some((r) => isActive(r.url))}
                >
                  {service.routes.map((subservice, subIndex) => {
                    return (
                      <MenuItem
                        active={isActive(subservice.url)}
                        key={subIndex}
                        component={<Link to={subservice?.url ? subservice?.url : ''} />}
                      >
                        <Box className="pro-sidebar-menu">
                          {isActive(subservice.url) && (
                            <Box className="icon-global" sx={{ svg: { paddingTop: '.2rem' } }}>
                              <ArrowForwardIcon />
                            </Box>
                          )}
                          {t(subservice.title)}
                        </Box>
                      </MenuItem>
                    )
                  })}
                </SubMenu>
              )
            } else {
              return (
                <MenuItem
                  icon={
                    <Box className="item-icon" style={{ color: themeGueno === 'light' ? '#66766B' : '#919D95' }}>
                      {service.icon}
                    </Box>
                  }
                  key={index}
                  component={<Link to={service?.url ? service?.url : ''} />}
                  active={isActive(service.url)}
                >
                  {t(service.title)}
                </MenuItem>
              )
            }
          })}
        </Menu>
      </Sidebar>
      {!userMaster?.isNuclea && (
        <Box className="custom-sidebar-bottom">
          {!collapsed && (
            <>
              <Box className="custom-sidebar-links-wrapper">
                <MuiLink disabled className="custom-sidebar-links">
                  <LaunchIcon />
                </MuiLink>

                <MuiLink
                  href="https://help.gueno.io/folders/Collection_root"
                  target="_blank"
                  className="custom-sidebar-links"
                >
                  <Typography variant="number">{t('common:helpCenter')}</Typography>
                </MuiLink>

                <Divider orientation="vertical" sx={{ borderWidth: '1px' }} />

                <MuiLink href="https://support.gueno.io/" target="_blank" className="custom-sidebar-links">
                  <Typography variant="number">{t('common:support')}</Typography>
                </MuiLink>
              </Box>
              <Box className="custom-sidebar-links-wrapper" style={{paddingLeft: '1.7rem'}}>
                <MuiLink href="https://trust.gueno.io/" target="_blank" className="custom-sidebar-links">
                  <Typography variant="number">{t('common:trustCenter')}</Typography>
                </MuiLink>

                <Divider orientation="vertical" sx={{ borderWidth: '1px' }} />

                <MuiLink href="https://status.gueno.io/" target="_blank" className="custom-sidebar-links">
                  <Typography variant="number">{t('common:statusPage')}</Typography>
                </MuiLink>
              </Box>
            </>
          )}

          <Box className="custom-sidebar-theme-toggle">
            {!collapsed && (
              <Box className="icon-global-medium custom-sidebar-theme-toggle">
                <NightsStayOutlinedIcon />
                {t('common:darkMode')}
              </Box>
            )}

            <IOSSwitch
              onChange={() => toggleTheme()}
              checked={themeGueno === 'light' ? false : true}
              variant="large"
              sx={{ marginRight: !collapsed ? '0' : '0.7rem' }}
              thememode={themeGueno}
            ></IOSSwitch>
          </Box>
        </Box>
      )}
    </Box>
  )
}
export default AppSidebar
